import { useState, useEffect, useRef } from "react";
import { useTheme } from "../../../theme";
import { BRAND_COLORS } from "../../../theme/colors.js";

export default function LoadingScreen({ onComplete, accentColor, accentGlow }) {
  const t = useTheme();
  const [pct, setPct] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading → done → exit
  const rafRef = useRef(null);
  const startRef = useRef(Date.now());
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const duration = 2200; // ms to reach 100
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-in-out curve: slow ramp then rapid finish
      const eased = progress < 0.6
        ? progress / 0.6 * 0.75
        : 0.75 + Math.pow((progress - 0.6) / 0.4, 0.6) * 0.25;
      const val = Math.round(Math.min(eased, 1) * 100);
      setPct(val);
      if (val >= 100 && !completedRef.current) {
        completedRef.current = true;
        setPhase("done");
        setTimeout(() => {
          setPhase("exit");
          setTimeout(() => onCompleteRef.current(), 600);
        }, 450);
        return;
      }
      if (!completedRef.current) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const isDone = phase === "done" || phase === "exit";
  const isExit = phase === "exit";

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: t.bg,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      opacity: isExit ? 0 : 1,
      pointerEvents: isExit ? "none" : "auto",
      transition: "opacity 0.55s ease-out",
    }}>
      {/* Ambient pulse */}
      <div style={{
        position: "absolute", width: "40vw", height: "40vw", borderRadius: "50%",
        background: isDone
          ? `radial-gradient(circle, rgba(${accentGlow},0.12) 0%, transparent 70%)`
          : `radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)`,
        transition: "background 0.6s ease",
        pointerEvents: "none",
      }} />

      {/* Percentage */}
      <span style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "clamp(48px, 12vw, 120px)",
        fontWeight: 300,
        letterSpacing: "-0.02em",
        color: isDone ? accentColor : "rgba(255,255,255,0.12)",
        transition: "color 0.4s ease",
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1,
        textShadow: isDone ? `0 0 40px rgba(${accentGlow},0.3)` : "none",
      }}>{pct}</span>

      {/* Unit label */}
      <span style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 11,
        letterSpacing: "0.3em",
        textTransform: "uppercase",
        color: isDone ? `rgba(${accentGlow},0.5)` : "rgba(255,255,255,0.06)",
        transition: "color 0.4s ease",
        marginTop: 12,
      }}>%</span>

      {/* Progress bar */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: 2,
        background: "rgba(255,255,255,0.03)",
      }}>
        <div style={{
          height: "100%",
          width: `${pct}%`,
          background: isDone
            ? accentColor
            : "rgba(255,255,255,0.08)",
          transition: isDone ? "background 0.4s ease" : "none",
          boxShadow: isDone ? `0 0 12px rgba(${accentGlow},0.4)` : "none",
        }} />
      </div>

      {/* Corner branding */}
      <div style={{
        position: "absolute", bottom: 32, right: 32,
        fontFamily: "'DM Mono', monospace",
        fontSize: 9, letterSpacing: "0.2em",
        color: "rgba(255,255,255,0.06)",
      }}>Three Letter Acronym</div>

      {/* Animated corner dots */}
      <div style={{
        position: "absolute", top: 32, left: 32,
        display: "flex", gap: 4,
      }}>
        {[BRAND_COLORS.red, BRAND_COLORS.blue, BRAND_COLORS.purple].map((c, i) => (
          <div key={i} style={{
            width: 4, height: 4, borderRadius: "50%",
            background: isDone ? c : "rgba(255,255,255,0.04)",
            transition: `background 0.3s ease ${i * 0.1}s`,
            animation: isDone ? "none" : `loadingPulse 1.5s ease ${i * 0.3}s infinite`,
          }} />
        ))}
      </div>
    </div>
  );
}
